import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { MailProvider } from "@/context/MailContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MyWork from "@/pages/MyWork";
import Team from "@/pages/Team";
import Cestino from "@/pages/Cestino";
import ArchivioClienti from "@/pages/ArchivioClienti";
import Backup from "@/pages/Backup";
import Shooting from "@/pages/Shooting";
import Mail from "@/pages/Mail";
import AmmTask from "@/pages/amministrazione/Task";
import AmmAnagrafica from "@/pages/amministrazione/Anagrafica";
import AmmPreventivi from "@/pages/amministrazione/Preventivi";
import AmmContratti from "@/pages/amministrazione/Contratti";
import AmmScadenze from "@/pages/amministrazione/Scadenze";
import Pagamenti from "@/pages/Pagamenti";
import AppLayout from "@/components/AppLayout";
import "@/App.css";

function Loading() {
  return (
    <div className="min-h-screen tp-bg flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-white/10 border-t-white/70 animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Shell({ children }) {
  return (
    <DataProvider>
      <MailProvider>
        <AppLayout>{children}</AppLayout>
      </MailProvider>
    </DataProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/"
        element={
          <Protected>
            <Shell>
              <Dashboard />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/il-mio-lavoro"
        element={
          <Protected>
            <Shell>
              <MyWork />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/team"
        element={
          <Protected>
            <Shell>
              <Team />
            </Shell>
          </Protected>
        }
      />
      <Route path="/clienti" element={<Navigate to="/" replace />} />
      <Route path="/amministrazione" element={<Navigate to="/amministrazione/task" replace />} />
      <Route
        path="/amministrazione/task"
        element={
          <Protected>
            <Shell>
              <AmmTask />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/amministrazione/anagrafica"
        element={
          <Protected>
            <Shell>
              <AmmAnagrafica />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/amministrazione/preventivi"
        element={
          <Protected>
            <Shell>
              <AmmPreventivi />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/amministrazione/contratti"
        element={
          <Protected>
            <Shell>
              <AmmContratti />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/amministrazione/scadenze"
        element={
          <Protected>
            <Shell>
              <AmmScadenze />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/pagamenti"
        element={
          <Protected>
            <Shell>
              <Pagamenti />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/cestino"
        element={
          <Protected>
            <Shell>
              <Cestino />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/backup"
        element={
          <Protected>
            <Shell>
              <Backup />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/archivio-clienti"
        element={
          <Protected>
            <Shell>
              <ArchivioClienti />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/shooting"
        element={
          <Protected>
            <Shell>
              <Shooting />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/mail"
        element={
          <AdminProtected>
            <Shell>
              <Mail />
            </Shell>
          </AdminProtected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster theme="dark" position="bottom-right" />
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}
